/**
 * ExternalUserSelector
 * Searchable dropdown for selecting external users
 */
import React, { useState, useMemo } from 'react';
import { Form, ListGroup, Button, Spinner, Badge } from 'react-bootstrap';
import { Search, Plus, User, Building, Mail } from 'lucide-react';
import { useExternalUsers } from '../hooks/useExternalUsers';
import { ExternalUser } from '../types/guestAccess';

interface ExternalUserSelectorProps {
    onSelect: (user: ExternalUser) => void;
    onCreateNew: () => void;
    selectedUser?: ExternalUser | null;
    disabledIds?: string[]; // UIDs to show as disabled with "Redan tillagd"
    /**
     * When provided, the selector uses this list instead of fetching its own.
     * Lets a parent that already has the external users loaded inject them and
     * avoid a redundant externalUserApi call on every mount (e.g. tab switch).
     */
    users?: ExternalUser[];
}

const ExternalUserSelector: React.FC<ExternalUserSelectorProps> = ({
    onSelect,
    onCreateNew,
    selectedUser,
    disabledIds = [],
    users: injectedUsers,
}) => {
    const isInjected = injectedUsers !== undefined;
    // Only fetch when no list was injected; the hook is always called to satisfy
    // the rules of hooks, but stays idle (loading=false) when disabled.
    const { users: fetchedUsers, loading, error } = useExternalUsers({ enabled: !isInjected });
    const users = injectedUsers ?? fetchedUsers;
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredUsers = useMemo(() => {
        if (!search.trim()) return users;
        const searchLower = search.toLowerCase();
        return users.filter(
            u =>
                u.name.toLowerCase().includes(searchLower) ||
                u.email.toLowerCase().includes(searchLower) ||
                (u.company || '').toLowerCase().includes(searchLower)
        );
    }, [users, search]);

    const handleSelectUser = (user: ExternalUser) => {
        onSelect(user);
        setSearch('');
        setIsOpen(false);
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center gap-2 p-3 border rounded">
                <Spinner animation="border" size="sm" />
                <span>Laddar användare...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-danger p-3 border border-danger rounded">
                {error}
            </div>
        );
    }

    return (
        <div className="external-user-selector">
            {selectedUser ? (
                <div className="selected-user p-3 border rounded bg-light d-flex align-items-center justify-content-between">
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <User size={18} />
                            <strong>{selectedUser.name}</strong>
                            {selectedUser.company && (
                                <Badge bg="secondary">{selectedUser.company}</Badge>
                            )}
                        </div>
                        <div className="text-muted small d-flex align-items-center gap-1 mt-1">
                            <Mail size={12} />
                            {selectedUser.email}
                        </div>
                    </div>
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => onSelect(null as unknown as ExternalUser)}
                    >
                        Ändra
                    </Button>
                </div>
            ) : (
                <>
                    <div className="position-relative">
                        <Search
                            size={18}
                            className="position-absolute"
                            style={{ left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                        />
                        <Form.Control
                            type="text"
                            placeholder="Sök på namn, e-post eller företag..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setIsOpen(true);
                            }}
                            onFocus={() => setIsOpen(true)}
                            style={{ paddingLeft: 40 }}
                        />
                    </div>

                    {isOpen && (
                        <div className="user-dropdown border rounded mt-3" style={{ maxHeight: 300, overflowY: 'auto' }}>
                            {filteredUsers.length === 0 ? (
                                <div className="p-3 text-center text-muted">
                                    {search ? 'Inga användare matchar din sökning' : 'Inga externa användare finns'}
                                </div>
                            ) : (
                                <ListGroup variant="flush">
                                    {filteredUsers.map((user) => {
                                        const isDisabled = disabledIds.includes(user.uid);
                                        return (
                                            <ListGroup.Item
                                                key={user.uid}
                                                action={!isDisabled}
                                                onClick={() => !isDisabled && handleSelectUser(user)}
                                                className="d-flex align-items-center gap-2"
                                                style={isDisabled ? { opacity: 0.6, cursor: 'default' } : undefined}
                                            >
                                                <User size={16} />
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <strong>{user.name}</strong>
                                                        {user.company && (
                                                            <small className="text-muted">
                                                                <Building size={12} className="me-1" />
                                                                {user.company}
                                                            </small>
                                                        )}
                                                        {isDisabled && (
                                                            <Badge bg="secondary" className="ms-auto">Redan tillagd</Badge>
                                                        )}
                                                    </div>
                                                    <small className="text-muted">{user.email}</small>
                                                </div>
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            )}
                            <div className="border-top p-2">
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => {
                                        setIsOpen(false);
                                        onCreateNew();
                                    }}
                                    className="w-100 text-start"
                                >
                                    <Plus size={16} className="me-2" />
                                    Lägg till ny extern användare
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ExternalUserSelector;
